/// <reference types="vite/client" />
import { useState, useEffect, useCallback } from "react";
import { useAgentSocketContext } from "./useAgentSocketContext";

// ─── Wire-format types (mirror src/api/swarm-state.ts SwarmData) ──────

export interface AgentStatus {
  id: string;
  name: string;
  type: string;
  status: "idle" | "active" | "running" | "paused" | "error";
  authorityLevel: number;
  tasksCompleted: number;
  tasksFailed: number;
  passRate: number;
  budgetUsedCents: number;
  budgetLimitCents: number;
}

export interface VerificationEntry {
  id: string;
  agentName: string;
  outputType: string;
  verdict: "PASS" | "FAIL" | "CONDITIONAL" | "BLOCK" | null;
  status: string;
  iteration: number;
  maxIterations: number;
  timestamp: string;
}

export interface DailyMetric {
  date: string;
  tasks: number;
  verifications: number;
  budgetCents: number;
}

export interface SwarmData {
  agents: AgentStatus[];
  verifications: VerificationEntry[];
  dailyMetrics: DailyMetric[];
  totals: {
    activeAgents: number;
    totalAgents: number;
    tasksCompleted: number;
    verificationPassRate: number;
    totalBudgetUsedCents: number;
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────

interface UseSwarmDataReturn {
  data: SwarmData | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /** When the data was last successfully loaded — null until first success. */
  lastUpdatedAt: Date | null;
}

// Phase 13 U1 #293 — initial bootstrap fetch + WS event subscriptions.
// Polling is gone ; refresh() still triggers a manual REST refetch (for
// the error-retry button).

export function useSwarmData(): UseSwarmDataReturn {
  const { subscribe } = useAgentSocketContext();
  const [data, setData] = useState<SwarmData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  const load = useCallback((signal?: AbortSignal) => {
    setLoading(true);
    setError(null);

    fetch("/api/swarm/state", { signal })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status}${body ? ` — ${body.slice(0, 200)}` : ""}`);
        }
        return res.json() as Promise<SwarmData>;
      })
      .then((json) => {
        setData(json);
        setLastUpdatedAt(new Date());
        setLoading(false);
      })
      .catch((err: Error) => {
        if (err.name === "AbortError") return;
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn(
            "[useSwarmData] /api/swarm/state fetch failed. Is the Worker running? Try `npm run dev:worker`.",
            err,
          );
        }
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // One-time bootstrap.
  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  // Live updates : merge in-place on heartbeat ticks per agent. Reusing
  // the agent name → status mapping keeps the diff minimal and avoids
  // re-rendering the whole tree on each event.
  useEffect(() => {
    const bump = () => setLastUpdatedAt(new Date());
    const unsubs = [
      subscribe("marketing.heartbeat_tick", bump),
      subscribe("support.heartbeat_tick", bump),
      subscribe("growth.heartbeat_tick", bump),
      subscribe("ceo.heartbeat_tick", bump),
      subscribe("verifier.heartbeat_tick", bump),
      subscribe("integrator.heartbeat_tick", bump),
      subscribe("engineering.heartbeat_tick", bump),
      subscribe("fundraising.heartbeat_tick", bump),
      // Verification stream — every emitted verdict bumps the dashboard.
      subscribe("verifier.verdict_emitted", bump),
      // CRM / sequences activity.
      subscribe("marketing.sequence_advanced", bump),
      subscribe("growth.pharow_enrolled", bump),
      subscribe("growth.lead_scored", bump),
      subscribe("ceo.decision_made", bump),
    ];
    return () => {
      unsubs.forEach((u) => u());
    };
  }, [subscribe]);

  const refresh = useCallback(() => {
    load();
  }, [load]);

  return { data, loading, error, refresh, lastUpdatedAt };
}
